import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";

const StudioVideoCard = ({ video, index }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { rootMargin: "120px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const toggleSound = () => {
    const el = ref.current;
    if (!el) return;
    el.muted = !el.muted;
    if (!el.muted) el.play().catch(() => {});
    setMuted(el.muted);
  };

  return (
    <motion.div
      data-testid={`studio-video-${video.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full max-w-2xl aspect-video rounded-[26px] overflow-hidden bg-[#141412] group mx-auto"
    >
      <video
        ref={ref}
        src={video.url}
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
      />
      <button
        data-testid={`studio-video-sound-${video.id}`}
        onClick={toggleSound}
        title={muted ? "Play with sound" : "Mute"}
        className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg hover:bg-white transition-colors"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </motion.div>
  );
};

export const StudioVideos = () => {
  const { t } = useContent();
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    api.get("/videos").then(({ data }) => setVideos(data || [])).catch(() => setVideos([]));
  }, []);

  if (!videos || videos.length === 0) return null;

  return (
    <section id="studio-videos" data-testid="studio-videos-section" className="relative bg-[#eae4da]/50 py-20 sm:py-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#c63f3e]"
        >
          {t("videos_kicker")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-extrabold tracking-[-0.02em] text-3xl sm:text-4xl mt-3 leading-[1.1]"
        >
          {t("videos_heading")}
        </motion.h2>
      </div>
      <div
        data-testid="studio-videos-row"
        className="mt-10 max-w-6xl mx-auto px-6 flex flex-wrap justify-center gap-6"
      >
        {videos.map((v, i) => (
          <StudioVideoCard key={v.id} video={v} index={i} />
        ))}
      </div>
    </section>
  );
};
